<template>
  <div>
    <ConsentModal v-if="authStore.needsConsent" @consent-given="authStore.createUser" />
    <!-- Background Canvas -->
    <canvas ref="canvasRef" id="web-background-canvas"></canvas>

    <!-- Centering Container -->
    <div class="content-wrapper">
      
      <!-- Login Card -->
      <div class="login-card">
        
        <!-- 1. Logo -->
        <img :src="logoSrc" alt="Inaubi Logo" class="w-10rem mb-6" />

        <!-- 2. Welcome Text -->
        <p class="text-xl text-center mb-6" :style="{ color: 'var(--text-color)' }">
          Accédez à votre boîte à outils Inaubi
        </p>

        <!-- 3. Google Sign-In Button -->
        <div id="google-signin-btn"></div>

      </div>
    </div>

    <!-- Palette Component -->
    <div class="palette-login-container">
      <AppPalette />
    </div>

    <!-- Unauthorized Domain Form Iframe -->
    <div v-if="showUnauthorizedDomainForm" class="iframe-container">
      <div class="iframe-header">
        <h3>Demande d'accès</h3>
        <button @click="authStore.hideUnauthorizedDomainForm()" class="close-iframe-btn">&times;</button>
      </div>
      <div class="iframe-wrapper">
        <iframe src="https://forms.gle/SHY5NfgMGRsiBRVc6" 
                width="100%" 
                height="100%" 
                frameborder="0" 
                marginheight="0" 
                marginwidth="0">
          Chargement…
        </iframe>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue';
import { useAuthStore } from '../stores/auth';
import api from '../services/api';
import AppPalette from '../components/AppPalette.vue';
import ConsentModal from '../components/ConsentModal.vue';
import { useThemeStore } from '../stores/theme';
import { storeToRefs } from 'pinia';

const authStore = useAuthStore();
const { showUnauthorizedDomainForm } = storeToRefs(authStore);
const canvasRef = ref(null);

// --- Logique Canvas (encapsulée) ---
let canvasAnimation = {};

const updateCanvasTheme = () => {
    if (canvasAnimation.updateCanvasColors) {
        canvasAnimation.updateCanvasColors();
    }
};

window.handleCredentialResponse = (response) => {
  if (response.credential) {
    authStore.login(response.credential);
  } else {
    console.error("Google Sign-In failed:", response);
  }
};

const themeStore = useThemeStore();
const { isDarkMode } = storeToRefs(themeStore);

const logoSrc = computed(() => {
  return isDarkMode.value ? '/inaubi_white.png' : '/inaubi_colour.png';
});

onMounted(async () => {
  try {
    const config = await api.getConfig();
    const CLIENT_ID = config.data.clientId;
    if (!CLIENT_ID) throw new Error("Could not retrieve Client ID from server.");

    window.google.accounts.id.initialize({
      client_id: CLIENT_ID,
      callback: window.handleCredentialResponse,
    });
    window.google.accounts.id.renderButton(
      document.getElementById('google-signin-btn'),
      { theme: 'outline', size: 'large', type: 'standard', text: 'signin_with' }
    );
  } catch (err) {
    console.error("Error during login setup:", err);
  }

  // Animation Canvas
  const canvas = canvasRef.value;
  const ctx = canvas.getContext('2d');
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;

  let particlesArray = [];
  let mouse = { x: null, y: null };

  const MAX_LINE_DISTANCE = 120;
  const MOUSE_INTERACTION_RADIUS = 150;

  function updateCanvasColors() {
      const styles = getComputedStyle(document.documentElement);
      canvasAnimation.particleRgb = styles.getPropertyValue('--particle-rgb').trim();
      canvasAnimation.lineRgb = styles.getPropertyValue('--line-rgb').trim();
      const accentHex = styles.getPropertyValue('--accent-color').trim();
      canvasAnimation.mouseLineColor = hexToRgba(accentHex, 0.3);
  }
  canvasAnimation.updateCanvasColors = updateCanvasColors;

  const observer = new MutationObserver(updateCanvasTheme);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'style'] });


  function hexToRgba(hex, opacity) {
      if (!hex) return `rgba(255, 255, 255, ${opacity})`;
      let c = hex.substring(1).split('');
      if (c.length === 3) c = [c[0], c[0], c[1], c[1], c[2], c[2]];
      c = '0x' + c.join('');
      return `rgba(${[(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',')},${opacity})`;
  }

  class Particle {
      constructor() {
          this.x = Math.random() * canvas.width;
          this.y = Math.random() * canvas.height;
          this.size = (Math.random() * 2) + 1;
          this.speedX = (Math.random() * 0.4) - 0.2;
          this.speedY = (Math.random() * 0.4) - 0.2;
      }
      draw() {
          ctx.fillStyle = `rgba(${canvasAnimation.particleRgb}, 0.8)`;
          ctx.beginPath();
          ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
          ctx.fill();
      }
      update() {
          this.x += this.speedX;
          this.y += this.speedY;
          if (this.x > canvas.width || this.x < 0) this.x = (this.x + canvas.width) % canvas.width;
          if (this.y > canvas.height || this.y < 0) this.y = (this.y + canvas.height) % canvas.height;
      }
  }

  function initParticles() {
      particlesArray = [];
      let numberOfParticles = (canvas.width * canvas.height) / 9000;
      for (let i = 0; i < numberOfParticles; i++) {
          particlesArray.push(new Particle());
      }
  }

  function connectParticles() {
      for (let i = 0; i < particlesArray.length; i++) {
          for (let j = i + 1; j < particlesArray.length; j++) {
              const distance = Math.hypot(particlesArray[i].x - particlesArray[j].x, particlesArray[i].y - particlesArray[j].y);
              if (distance < MAX_LINE_DISTANCE) {
                  const lineOpacity = 1 - (distance / MAX_LINE_DISTANCE);
                  ctx.strokeStyle = `rgba(${canvasAnimation.lineRgb}, ${lineOpacity * 0.1})`;
                  ctx.lineWidth = 0.5;
                  ctx.beginPath();
                  ctx.moveTo(particlesArray[i].x, particlesArray[i].y);
                  ctx.lineTo(particlesArray[j].x, particlesArray[j].y);
                  ctx.stroke();
              }
          }
          if (mouse.x && mouse.y) {
              const distanceMouse = Math.hypot(particlesArray[i].x - mouse.x, particlesArray[i].y - mouse.y);
              if (distanceMouse < MOUSE_INTERACTION_RADIUS) {
                  ctx.strokeStyle = canvasAnimation.mouseLineColor;
                  ctx.lineWidth = 1;
                  ctx.beginPath();
                  ctx.moveTo(particlesArray[i].x, particlesArray[i].y);
                  ctx.lineTo(mouse.x, mouse.y);
                  ctx.stroke();
              }
          }
      }
  }

  function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particlesArray.forEach(p => { p.update(); p.draw(); });
      connectParticles();
      requestAnimationFrame(animate);
  }

  window.addEventListener('mousemove', e => { mouse.x = e.x; mouse.y = e.y; });
  window.addEventListener('mouseout', () => { mouse.x = null; mouse.y = null; });
  window.addEventListener('resize', () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initParticles();
  });

  updateCanvasColors();
  initParticles();
  animate();
});
</script>

<style scoped>
#web-background-canvas {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0; /* Placé en arrière-plan */
}

.content-wrapper {
  position: relative;
  z-index: 10; /* Placé au-dessus du canvas */
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-card {
  background-color: var(--form-bg);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  padding: 2rem;
  border-radius: 0.75rem; /* rounded-xl */
  box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1); /* shadow-2xl */
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: background-color 0.3s ease;

  /* Responsive width adjustments */
  width: 90%;
  max-width: 384px; /* Equivalent to Tailwind's max-w-sm */
}

@media (min-width: 1024px) { /* lg breakpoint */
  .login-card {
    width: 45%;
    max-width: 450px;
  }
}

@media (min-width: 1280px) { /* xl breakpoint */
  .login-card {
    width: 35%;
    max-width: 500px;
  }
}

@media (min-width: 1536px) { /* 2xl breakpoint */
  .login-card {
    width: 25%;
    min-width: 384px; /* Ensure it doesn't get too small */
    max-width: 550px;
  }
}

.palette-login-container {
    position: fixed;
    bottom: 1.5rem;
    left: 1.5rem;
    z-index: 20;
}

.iframe-container {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 60%;
  max-height: 500px;
  background-color: var(--background-color);
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  border-top-left-radius: 1rem;
  border-top-right-radius: 1rem;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease-in-out;
  transform: translateY(0);
}

.iframe-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1.5rem;
  border-bottom: 1px solid var(--border-color);
}

.iframe-header h3 {
  margin: 0;
  font-size: 1.1rem;
  color: var(--text-color);
}

.close-iframe-btn {
  background: none;
  border: none;
  font-size: 1.75rem;
  font-weight: bold;
  color: var(--text-color-secondary);
  cursor: pointer;
  padding: 0.5rem;
  line-height: 1;
}

.close-iframe-btn:hover {
  color: var(--accent-color);
}

.iframe-wrapper {
  flex-grow: 1;
  overflow: hidden;
}

.iframe-wrapper iframe {
  border: none;
}
</style>